import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { TextButton } from '@shipt/design-system-buttons'
import { screenSizes } from '@shipt/design-system-themes'
import styled from 'styled-components'
import { getHeadingElement } from '@/features/shared/utils/getHeadingElement'
import { Body, Headline } from '@shipt/design-system-typography'
import { Column, Row } from '@shipt/design-system-layouts'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { InfoButton } from '@/features/shared/components/InfoButton'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { DynamicStoresCarouselDisclaimerDrawer } from '@/features/cms/components/CMS/components/BrandAmplification/StoresCarouselDisclaimerDrawer/DynamicStoresCarouselDisclaimerDrawer'
import {
  createStoresCarousel,
  type StoresCarouselHeaderProps,
} from '@/features/cms/components/CMS/components/BrandAmplification/StoresCarouselBase'
import { StoresCarouselMobileHeader } from '@/features/cms/components/CMS/components/BrandAmplification/StoresCarouselHeader'
import { useExperiment } from '@/features/shared/services/Experiments/hooks'
import { ExperimentType } from '@shared/types/experiments'
import { theme } from '@/features/shared/constants/shiptTheme'

const DynamicHeader = ({
  heading,
  subheading,
  disclaimer,
  image,
  seoHeadLevel,
  view_all_link,
  handleViewAll,
  isDesktop,
}: StoresCarouselHeaderProps & { isDesktop?: boolean }) => {
  const { openDialog } = useHandleDialog()
  const { heading: disclaimerHeading, content: disclaimerContent } = disclaimer?.data ?? {}
  const hasDisclaimer = !!disclaimerHeading && !!disclaimerContent?.elements
  const { trackCMSElementClicked } = useCMSTrackingEvents()

  const openDisclaimerDrawer = () => {
    if (!hasDisclaimer) return
    const content = disclaimerContent.elements
    trackCMSElementClicked({ content: 'info_icon' })
    openDialog(DynamicStoresCarouselDisclaimerDrawer, {
      title: disclaimerHeading,
      content,
    })
  }

  return (
    <Row
      align={isDesktop ? 'end' : 'center'}
      margin={isDesktop ? ['lg', 0] : ['sm', 'lg']}
      spacing={isDesktop ? undefined : 'sm'}
      justify={isDesktop ? 'space-between' : undefined}
    >
      <Column spacing="xs">
        <Headline as={getHeadingElement(seoHeadLevel)}>{heading ?? 'Stores near you'}</Headline>

        <Subheading variant="secondary" size={isDesktop ? 'md' : 'sm'}>
          {subheading}
          {!!hasDisclaimer && (
            <>
              {/* non-breaking space to prevent icon from being on its own line */}
              {'\u00A0'}
              <InfoButton aria-label="open for more information" onClick={openDisclaimerDrawer} />
            </>
          )}
        </Subheading>
      </Column>
      <Row align="end" spacing={isDesktop ? '4xl' : undefined} flexShrink={0}>
        {!!image && (
          <DynamicImage src={image.src} width={isDesktop ? 390 : 152} height={130} alt="" />
        )}
        {/* On Desktop show the view all button and right align image */}
        {isDesktop && view_all_link ? (
          <TextButton
            onClick={handleViewAll}
            aria-label="View all stores"
            className="desktop-button"
          >
            {view_all_link.text}
          </TextButton>
        ) : undefined}
      </Row>
    </Row>
  )
}

const DesktopHeader = (props: StoresCarouselHeaderProps) => {
  const { active: isHolidayGhpActive } = useExperiment(ExperimentType.HOLIDAY_GHP)

  return (
    <DynamicHeader
      {...props}
      heading={isHolidayGhpActive ? (props.carousel_title ?? props.heading) : props.heading}
      isDesktop
    />
  )
}

const MobileHeader = (props: StoresCarouselHeaderProps) => (
  <>
    <DynamicHeader {...props} image={props.mobile_image ?? props.image} />
    {!!props.carousel_title && (
      <StoresCarouselMobileHeader
        {...props}
        heading={props.carousel_title}
        subheading={undefined}
      />
    )}
  </>
)

type DynamicCarouselWrapperProps = React.ComponentProps<typeof GridLayoutContainer> & {
  background_color_hex?: string
}

const DynamicCarouselWrapper = (props: DynamicCarouselWrapperProps) => {
  const { active: isHolidayGhpActive } = useExperiment(ExperimentType.HOLIDAY_GHP, {
    autoTrack: true,
  })

  return <CarouselWrapper {...props} $isHolidayGhpActive={isHolidayGhpActive} />
}

const CarouselWrapper = styled(GridLayoutContainer)<{
  background_color_hex?: string
  $isHolidayGhpActive: boolean
}>`
  background-color: ${({ $isHolidayGhpActive, background_color_hex }) =>
    $isHolidayGhpActive ? background_color_hex : 'none'};
  margin-bottom: ${({ $isHolidayGhpActive, background_color_hex }) =>
    $isHolidayGhpActive && background_color_hex ? '1rem' : undefined};

  @media ${screenSizes.smDesktop} {
    --content-max-width: 1080px;
  }

  & .stores-carousel-desktop {
    margin-bottom: 1rem;
    margin-top: 1rem;

    @media ${screenSizes.lgMobile} {
      display: block;
 

      & .tablet-button {
        display: flex;
      }
    }

    @media ${screenSizes.tablet} {
      grid-column: content;

      & .desktop-button {
        display: flex;
      }
    }
  }

  & .stores-carousel-mobile {
    grid-column: full-width;
    margin-bottom: 1rem;
  }

    &[data-has-bg='true'] {
    /* this variable is used to set the gradient color behind the nav buttons inside the Carousel */
    --carousel-container-bg: transparent;
    --carousel-nav-button-offset: -10%;
    
    & .stores-carousel-desktop {
      & .stores-carousel-card {
       --carousel-container-bg: transparent;
          background-color: ${({ background_color_hex }) => background_color_hex};
          border: ${({ $isHolidayGhpActive }) => ($isHolidayGhpActive ? 'none' : 'inherit')};
      padding: 12px 32px;
     
      }
    }

     & .stores-carousel-mobile {
      background-color: ${({ background_color_hex }) => background_color_hex};
      padding: 12px 0;
      }
  }
`

const DynamicImage = styled(Image)`
  height: auto;
`
const Subheading = styled(Body)`
  button {
    padding-left: 4px;
    padding-right: 4px;
    padding-bottom: 4px;
    margin: 0;

    svg {
      color: ${theme.color.content.base.neutral.secondary};
    }
  }
`

export const DynamicStoresCarousel = createStoresCarousel({
  Wrapper: DynamicCarouselWrapper,
  DesktopHeader,
  MobileHeader,
  wrapDesktopInCard: true,
})
